/**
 * Reference Data Sync — a top-level admin tab (Fleet operations). Push a SOURCE
 * reference/lookup table into many TARGET databases that hold a same-named table.
 * Rows are matched by the source table's PRIMARY KEY; only columns present (and
 * writable) on both sides are synced.
 *
 *   Upsert — insert new + update changed rows (never deletes). Additive/safe.
 *   Mirror — upsert AND delete target rows missing from source. Destructive;
 *            gated behind an extra confirm.
 *
 * Preview shows per-target insert/update/delete counts; apply re-derives the plan
 * server-side and runs each target in its own transaction. Props: { active }.
 */
import { createSignal, createResource, createMemo, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import { api } from '../lib/api.js';
import { previewRefSync, applyRefSync } from '../lib/refsync.js';
import { toast } from '../lib/toast.js';

const SYSTEM_DBS = ['mysql', 'information_schema', 'performance_schema', 'sys'];

async function fetchDbs(id) {
  if (!id) return [];
  try {
    const { databases } = await api.get(`/api/connections/${id}/databases`);
    return (databases ?? []).map((d) => d.name).filter((n) => !SYSTEM_DBS.includes(n));
  } catch { return []; }
}

export default function RefSyncPanel() {
  const [conns] = createResource(async () => {
    const { connections } = await api.get('/api/connections');
    return connections ?? [];
  });
  const connName = (id) => conns()?.find((c) => String(c.id) === String(id))?.name || `#${id}`;

  // ── Source ────────────────────────────────────────────────────────────────
  const [srcConn, setSrcConn] = createSignal('');
  const [srcDb, setSrcDb] = createSignal('');
  const [srcTable, setSrcTable] = createSignal('');
  const [srcDbList, setSrcDbList] = createSignal(null);
  const [srcTableList, setSrcTableList] = createSignal(null);

  const onSrcConn = async (id) => {
    setSrcConn(id); setSrcDb(''); setSrcTable(''); setSrcTableList(null); resetResults();
    setSrcDbList(id ? await fetchDbs(id) : null);
  };
  const onSrcDb = async (db) => {
    setSrcDb(db); setSrcTable(''); resetResults();
    if (!db) { setSrcTableList(null); return; }
    try {
      const r = await api.get(`/api/connections/${srcConn()}/databases/${encodeURIComponent(db)}/objects`);
      setSrcTableList((r.tables ?? []).map((t) => t.name));
    } catch (e) { setSrcTableList([]); toast.error(e.message); }
  };

  const [mode, setMode] = createSignal('upsert'); // 'upsert' | 'mirror'

  // ── Targets ───────────────────────────────────────────────────────────────
  const [targets, setTargets] = createStore([]); // [{ conn, db }]
  const [tConn, setTConn] = createSignal('');
  const [tDb, setTDb] = createSignal('');
  const [tDbList, setTDbList] = createSignal(null);
  const onTConn = async (id) => { setTConn(id); setTDb(''); setTDbList(id ? await fetchDbs(id) : null); };

  const addTarget = () => {
    if (!tConn() || !tDb()) { toast.info('Pick a connection and database to add.'); return; }
    const conn = Number(tConn());
    if (conn === Number(srcConn()) && tDb() === srcDb()) { toast.warn('That is the source database.'); return; }
    if (targets.some((t) => t.conn === conn && t.db === tDb())) { toast.info('Target already added.'); return; }
    setTargets([...targets, { conn, db: tDb() }]);
    setTDb('');
    resetResults();
  };
  const removeTarget = (i) => { setTargets(targets.filter((_, idx) => idx !== i)); resetResults(); };

  // ── Preview / Apply ─────────────────────────────────────────────────────────
  const [preview, setPreview] = createSignal(null);
  const [previewing, setPreviewing] = createSignal(false);
  const [confirmApply, setConfirmApply] = createSignal(false);
  const [applying, setApplying] = createSignal(false);
  const [applyResults, setApplyResults] = createSignal(null);
  const [err, setErr] = createSignal('');
  const resetResults = () => { setPreview(null); setApplyResults(null); setConfirmApply(false); };

  const ready = () => srcConn() && srcDb() && srcTable() && targets.length > 0;

  const runPreview = async () => {
    if (!ready()) { setErr('Pick a source table and add at least one target.'); return; }
    setErr(''); setPreviewing(true); setApplyResults(null); setConfirmApply(false);
    try {
      const r = await previewRefSync(srcConn(), srcDb(), srcTable(), mode(), targets);
      setPreview(r);
    } catch (e) { setErr(e.message); }
    finally { setPreviewing(false); }
  };

  const totals = createMemo(() => {
    const t = { insert: 0, update: 0, delete: 0, errors: 0 };
    for (const r of (preview()?.targets || [])) {
      if (!r.ok) { t.errors++; continue; }
      t.insert += r.insert; t.update += r.update; t.delete += r.delete;
    }
    return t;
  });

  const runApply = async () => {
    setConfirmApply(false); setApplying(true);
    try {
      const r = await applyRefSync(srcConn(), srcDb(), srcTable(), mode(), targets, 'continue');
      setApplyResults(r);
      const ok = (r.targets || []).filter((x) => x.ok).length;
      const failed = (r.targets || []).length - ok;
      if (failed) toast.warn(`${ok} target(s) synced · ${failed} failed.`);
      else toast.success(`Synced ${ok} target(s).`);
      runPreview(); // refresh counts (should now be ~0 inserts/updates/deletes)
    } catch (e) { toast.error(`Apply failed: ${e.message}`); }
    finally { setApplying(false); }
  };

  const applyResultFor = (t) => (applyResults()?.targets || []).find((x) => x.conn === t.conn && x.db === t.db);

  return (
    <div class="qc-panel rs-panel">
      <div class="qc-head">
        <div>
          <h2>Reference Data Sync</h2>
          <p class="muted">Push a source reference table into many target databases — matched by primary key. Upsert (safe) or Mirror (also deletes extras).</p>
        </div>
      </div>

      {/* Source */}
      <section class="rs-section">
        <div class="rs-section-title">1 · Source table</div>
        <div class="rs-src-grid">
          <label class="cr-field">
            <span class="cr-field-label">Connection</span>
            <select class="input" value={srcConn()} onChange={(e) => onSrcConn(e.currentTarget.value)}>
              <option value="">— connection —</option>
              <For each={conns() ?? []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
            </select>
          </label>
          <label class="cr-field">
            <span class="cr-field-label">Database</span>
            <select class="input" value={srcDb()} onChange={(e) => onSrcDb(e.currentTarget.value)} disabled={!srcDbList()}>
              <option value="">— database —</option>
              <For each={srcDbList() ?? []}>{(n) => <option value={n}>{n}</option>}</For>
            </select>
          </label>
          <label class="cr-field">
            <span class="cr-field-label">Table</span>
            <select class="input" value={srcTable()} onChange={(e) => { setSrcTable(e.currentTarget.value); resetResults(); }} disabled={!srcTableList()}>
              <option value="">— table —</option>
              <For each={srcTableList() ?? []}>{(n) => <option value={n}>{n}</option>}</For>
            </select>
          </label>
        </div>
      </section>

      {/* Mode */}
      <section class="rs-section">
        <div class="rs-section-title">2 · Mode</div>
        <div class="col-expand-modes rs-modes">
          <button class="col-expand-mode" classList={{ active: mode() === 'upsert' }} onClick={() => { setMode('upsert'); resetResults(); }}>
            Upsert
          </button>
          <button class="col-expand-mode" classList={{ active: mode() === 'mirror' }} onClick={() => { setMode('mirror'); resetResults(); }}>
            Mirror
          </button>
        </div>
        <span class="muted rs-mode-note">
          {mode() === 'upsert'
            ? 'Inserts new rows and updates changed ones. Never deletes — targets keep extra rows.'
            : '⚠ Also DELETES target rows missing from the source, making each target identical to it.'}
        </span>
      </section>

      {/* Targets */}
      <section class="rs-section">
        <div class="rs-section-title">3 · Targets <span class="muted">({targets.length})</span></div>
        <div class="rs-target-add">
          <select class="input" value={tConn()} onChange={(e) => onTConn(e.currentTarget.value)}>
            <option value="">— connection —</option>
            <For each={conns() ?? []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
          </select>
          <select class="input" value={tDb()} onChange={(e) => setTDb(e.currentTarget.value)} disabled={!tDbList()}>
            <option value="">— database —</option>
            <For each={tDbList() ?? []}>{(n) => <option value={n}>{n}</option>}</For>
          </select>
          <button class="btn btn-ghost" onClick={addTarget} disabled={!tConn() || !tDb()}>＋ Add target</button>
        </div>
        <Show when={targets.length} fallback={<div class="muted rs-empty">No targets yet — add the databases to sync into.</div>}>
          <div class="rs-target-list">
            <For each={targets}>
              {(t, i) => (
                <div class="rs-target-chip">
                  <span class="mono">{connName(t.conn)} · {t.db}</span>
                  <Show when={srcTable()}><span class="muted"> → {srcTable()}</span></Show>
                  <button class="rs-chip-x" title="Remove" onClick={() => removeTarget(i())}>×</button>
                </div>
              )}
            </For>
          </div>
        </Show>
      </section>

      <Show when={err()}><div class="form-error" style="margin:8px 0;">{err()}</div></Show>

      <div class="rs-actions">
        <button class="btn" onClick={runPreview} disabled={previewing() || !ready()}>
          {previewing() ? '⏳ Previewing…' : '👁 Preview'}
        </button>
        <Show when={preview()}>
          <button class={`btn ${mode() === 'mirror' ? 'btn-danger-solid' : ''}`} onClick={() => setConfirmApply(true)}
                  disabled={applying() || totals().insert + totals().update + totals().delete === 0}>
            {applying() ? '⏳ Applying…' : (mode() === 'mirror' ? '⚠ Apply (mirror)' : '⤓ Apply')}
          </button>
        </Show>
      </div>

      {/* Confirm */}
      <Show when={confirmApply()}>
        <div class="cr-confirm rs-confirm" classList={{ 'rs-confirm-danger': mode() === 'mirror' }}>
          <Show when={mode() === 'mirror'} fallback={
            <span>Apply to {preview().targets.filter((t) => t.ok).length} target(s)? <strong>{totals().insert}</strong> inserts · <strong>{totals().update}</strong> updates.</span>
          }>
            <span>⚠ <strong>Mirror</strong> will <strong>DELETE {totals().delete}</strong> row(s), insert {totals().insert}, update {totals().update} across {preview().targets.filter((t) => t.ok).length} target(s). Deletions cannot be undone.</span>
          </Show>
          <div class="spacer" />
          <button class="btn btn-sm btn-ghost" onClick={() => setConfirmApply(false)}>Cancel</button>
          <button class="btn btn-sm btn-danger-solid" onClick={runApply}>Confirm{mode() === 'mirror' ? ' mirror' : ''}</button>
        </div>
      </Show>

      {/* Preview / results matrix */}
      <Show when={preview()}>
        <section class="rs-section">
          <div class="rs-section-title">
            Preview
            <span class="muted"> · source {preview().source.rowCount.toLocaleString()} rows · PK ({(preview().source.pkColumns || []).join(', ')})</span>
          </div>
          <div class="aa-summary">
            <span class="rs-pill rs-pill-ins">{totals().insert} insert</span>
            <span class="rs-pill rs-pill-upd">{totals().update} update</span>
            <Show when={mode() === 'mirror'}><span class="rs-pill rs-pill-del">{totals().delete} delete</span></Show>
            <Show when={totals().errors}><span class="rs-pill rs-pill-err">{totals().errors} error</span></Show>
          </div>
          <div class="tc-table-wrap">
            <table class="tc-table rs-table">
              <thead>
                <tr>
                  <th>Target</th>
                  <th class="tc-num">Insert</th>
                  <th class="tc-num">Update</th>
                  <Show when={mode() === 'mirror'}><th class="tc-num">Delete</th></Show>
                  <th class="tc-num">Same</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <For each={preview().targets}>
                  {(t) => {
                    const ar = applyResultFor(t);
                    return (
                      <tr class="tc-row" classList={{ 'rs-row-err': !t.ok }}>
                        <td class="mono">{connName(t.conn)} · {t.db}</td>
                        <Show when={t.ok} fallback={<td colSpan={mode() === 'mirror' ? 5 : 4} class="rs-err-cell">{t.error}</td>}>
                          <td class="tc-num">{t.insert}</td>
                          <td class="tc-num">{t.update}</td>
                          <Show when={mode() === 'mirror'}><td class="tc-num">{t.delete}</td></Show>
                          <td class="tc-num muted">{t.same}</td>
                          <td>
                            <Show when={ar} fallback={
                              <Show when={t.insert + t.update + t.delete === 0} fallback={<span class="rs-badge rs-badge-pending">pending</span>}>
                                <span class="rs-badge rs-badge-insync">in sync</span>
                              </Show>
                            }>
                              <Show when={ar.ok} fallback={<span class="rs-badge rs-badge-fail" title={ar.error}>failed</span>}>
                                <span class="rs-badge rs-badge-done">✓ {ar.inserted}+ {ar.updated}~ {mode() === 'mirror' ? `${ar.deleted}-` : ''}</span>
                              </Show>
                            </Show>
                          </td>
                        </Show>
                      </tr>
                    );
                  }}
                </For>
              </tbody>
            </table>
          </div>
          <Show when={preview().targets.some((t) => (t.droppedColumns || []).length)}>
            <p class="cr-footnote muted">
              Some targets are missing source columns (or have them generated); those columns are skipped on that target.
              Hover a target row for detail. Sizes/keys are matched by primary key: <strong>{(preview().source.pkColumns || []).join(', ')}</strong>.
            </p>
          </Show>
        </section>
      </Show>
    </div>
  );
}
